import React from "react";
import { Outlet, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Drawer as DrawerPrimitive } from "vaul";
import { Menu } from "lucide-react";
import AdminSidebar, { SIDEBAR_WIDTH_CLASS, SidebarBody } from "./AdminSidebar";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getTokenPayload } from "../../authStore";
import { calendarNavState } from "./workOrganizationNav";

/**
 * Mobile/tablet top-bar title source (M2). Longest-prefix match.
 * Detail routes inherit their section's title automatically.
 * Unmatched admin routes fall back to "Vantio" (per Tarkan adjustment 1).
 */
const ADMIN_ROUTE_TITLES = [
  // OPS-NAV1: the Arbeitsorganisation surfaces. All four fell through to
  // "Vantio" before, which is what the mobile top bar showed while the
  // sidebar named them properly.
  ["/admin/todo-lists", "To-do-Listen"],
  ["/admin/tasks", "Aufgaben"],
  ["/admin/calendar", "Kalender"],
  ["/admin/responsibilities", "Verantwortlichkeiten"],
  ["/admin/work-reports", "Rapporte"],
  ["/admin/inventory", "Inventar"],
  ["/admin/documents", "Dokumente"],
  ["/admin/applications", "Bewerbungen"],
  ["/admin/tenancies", "Mietverhältnisse"],
  ["/admin/nachweise", "Nachweise & Pflichten"],
  ["/admin/tenants", "Mieter"],
  // More specific first. `/admin/invoices` now titles the LEGACY invoice list,
  // and this map matches on prefix — so without its own entry the create wizard
  // at /admin/invoices/new would announce itself as "Historische Rechnungen"
  // while the user is making a new one. The status filters (/open, /paid,
  // /overdue) and the detail route DO inherit it, correctly: they are views of
  // that same historical list.
  ["/admin/invoices/new", "Neue Rechnung"],
  ["/admin/invoices", "Historische Rechnungen"],
  ["/admin/properties", "Liegenschaften"],
  ["/admin/apartments", "Einheiten"],
  ["/admin/users", "Benutzer"],
  ["/admin/email-settings", "E-Mail-Absender"],
  ["/admin/portfolio-map", "Portfolio-Karte"],
  ["/admin/operations", "Co-Living"],
  ["/admin/business-apartments-dashboard", "Business Apartments"],
  ["/admin/billing-settings", "Rechnungseinstellungen"],
  ["/admin/belege", "Belege"],
  ["/admin/kautionen", "Kautionen & Sicherheiten"],
  ["/admin/revenue-analytics", "Umsatz & Miete"],
  // More specific first: /admin/bexio/drafts must not fall through to "Bexio".
  ["/admin/bexio/drafts", "Rechnungsentwürfe"],
  ["/admin/bexio/sync-runs", "Sync-Läufe"],
  ["/admin/bexio/settings", "Bexio-Einstellungen"],
  ["/admin/bexio", "Bexio"],
];

export function adminRouteToTitle(pathname, search = "") {
  // OPS-NAV1: Rapporte is the calendar in reports mode, so the title has
  // to read the query too — otherwise the top bar says "Kalender" on the
  // page the sidebar just called Rapporte. Same helper the sidebar uses,
  // so the two can never disagree about which mode is showing.
  if (calendarNavState(pathname, search).reports) {
    return "Rapporte";
  }
  for (const [prefix, title] of ADMIN_ROUTE_TITLES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return title;
    }
  }
  return "Vantio";
}

function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === "/admin/login";
  const { isAuthenticated, loading, user, isImpersonating, impersonatedOrganizationName, exitImpersonation } =
    useAuth();
  const { theme } = useTheme();

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // Defensive close-on-route-change: catches NavLink clicks AND any
  // future programmatic navigation triggered from inside the drawer
  // (logout, theme toggle, etc.). Per Tarkan M2 adjustment #2.
  //
  // OPS-NAV1 added `search`: Kalender → Rapporte is the SAME pathname
  // with a different query, so keyed on the pathname alone the effect
  // would not re-run and the drawer would stay open over the page the
  // user just navigated to.
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const isDark = theme === "dark";
  const shellClass = isDark
    ? "relative z-[1] bg-[#060b14] text-[#f8fafc]"
    : "relative z-[1] bg-[#f8fafc] text-[#0f172a]";
  const mainClass = isDark ? "bg-[#060b14]" : "bg-[#f8fafc]";
  const scrollbarStyle = isDark
    ? { scrollbarColor: "rgba(255,255,255,0.12) #060b14" }
    : { scrollbarColor: "rgba(15,23,42,0.2) #f8fafc" };
  const loadingTextClass = isDark ? "text-[#93a4bf]" : "text-[#64748b]";

  // Drawer + mobile top bar styling — mirrors the persistent sidebar
  // shell so the drawer-mounted SidebarBody renders identically to the
  // lg:+ sidebar (same bg, padding, border, text color).
  const drawerContentClass = isDark
    ? `fixed inset-y-0 left-0 z-50 flex h-full ${SIDEBAR_WIDTH_CLASS} flex-col border-r border-white/[0.08] bg-[#0b1220] px-3.5 pb-4 pt-6 text-[#f8fafc] outline-none`
    : `fixed inset-y-0 left-0 z-50 flex h-full ${SIDEBAR_WIDTH_CLASS} flex-col border-r border-zinc-200 bg-white px-3.5 pb-4 pt-6 text-zinc-900 outline-none`;

  const topBarClass = isDark
    ? "lg:hidden sticky top-0 z-40 flex h-12 items-center gap-3 border-b border-white/[0.08] bg-[#060b14]/95 px-3 backdrop-blur-md"
    : "lg:hidden sticky top-0 z-40 flex h-12 items-center gap-3 border-b border-black/[0.08] bg-white/95 px-3 backdrop-blur-md";

  const hamburgerBtnClass = isDark
    ? "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-white/[0.1] bg-transparent text-[#f8fafc] hover:bg-white/[0.06]"
    : "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-black/[0.1] bg-transparent text-[#0f172a] hover:bg-black/[0.04]";

  const titleClass = isDark
    ? "min-w-0 flex-1 truncate text-center text-[14px] font-semibold text-[#f8fafc]"
    : "min-w-0 flex-1 truncate text-center text-[14px] font-semibold text-[#0f172a]";

  const pageTitle = adminRouteToTitle(location.pathname, location.search);

  const payload = getTokenPayload();
  const isImpersonatingFromToken = !!payload?.impersonated_by;

  const supportBanner =
    isImpersonating && !isLoginPage ? (
      <div
        className={
          isDark
            ? "border-b border-amber-500/25 bg-amber-500/[0.12] px-4 py-2.5 text-[13px] text-[#fde68a]"
            : "border-b border-amber-400/30 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-950"
        }
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <span className="flex min-w-0 flex-col gap-0.5">
            <span>
              🛠 Support-Modus
              {isImpersonatingFromToken && payload?.impersonator_email
                ? ` — von ${payload.impersonator_email}`
                : null}
            </span>
            <span
              className={
                isDark ? "text-[11px] text-[#fde68a]/80" : "text-[11px] text-amber-950/80"
              }
            >
              Organisation: <strong>{impersonatedOrganizationName || "—"}</strong>
            </span>
          </span>
          <button
            type="button"
            onClick={() =>
              exitImpersonation().then(() => navigate("/platform/organizations", { replace: true }))
            }
            className={
              isDark
                ? "shrink-0 rounded-[8px] border border-amber-400/35 bg-[#0b1220] px-3 py-1.5 text-[12px] font-semibold text-[#fde68a] hover:bg-[#111a2e]"
                : "shrink-0 rounded-[8px] border border-amber-600/25 bg-white px-3 py-1.5 text-[12px] font-semibold text-amber-950 hover:bg-amber-100/80"
            }
          >
            Zurück zur Plattform
          </button>
        </div>
      </div>
    ) : null;

  // Login route: keep today's behavior pixel-identical, no top bar,
  // no drawer machinery (login page has its own full-viewport UI).
  if (isLoginPage) {
    return (
      <div className={`flex min-h-screen ${shellClass}`}>
        <div
          className={`flex min-h-screen flex-1 flex-col overflow-auto ${mainClass}`}
          style={scrollbarStyle}
        >
          {supportBanner}
          <div className="p-4 md:p-10">
            <Outlet />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center ${shellClass} ${loadingTextClass}`}
      >
        <p>Lade …</p>
      </div>
    );
  }

  if (user && user.role === "platform_admin") {
    return <Navigate to="/platform" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className={`flex min-h-screen ${shellClass}`}>
      {/* Persistent sidebar — visible at lg: and above, identical to pre-M2. */}
      <aside className="hidden lg:flex">
        <AdminSidebar />
      </aside>

      {/* Mobile/tablet drawer — controls the same SidebarBody below lg:.
          Direction is "left" (slide-in from edge), modal so the rest of
          the page is non-interactive while open. shouldScaleBackground
          is false because that effect targets bottom-sheets. */}
      <DrawerPrimitive.Root
        direction="left"
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        shouldScaleBackground={false}
      >
        <DrawerPrimitive.Portal>
          <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
          <DrawerPrimitive.Content className={drawerContentClass}>
            <DrawerPrimitive.Title className="sr-only">Navigation</DrawerPrimitive.Title>
            <SidebarBody />
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Portal>
      </DrawerPrimitive.Root>

      <div
        className={`flex min-h-screen flex-1 flex-col overflow-auto ${mainClass}`}
        style={scrollbarStyle}
      >
        {/* Mobile/tablet top bar — visible below lg: only. */}
        <header className={topBarClass}>
          <button
            type="button"
            aria-label="Menü öffnen"
            onClick={() => setDrawerOpen(true)}
            className={hamburgerBtnClass}
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>
          <span className={titleClass}>{pageTitle}</span>
          {/* Reserved overflow slot — same width as hamburger so the
              title stays optically centered. M3+ may add actions here. */}
          <span className="h-9 w-9 shrink-0" aria-hidden />
        </header>

        {supportBanner}
        <div className="p-4 md:p-10">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
